export default DesktopNotifications;

import { BellIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { navigate } from 'vike/client/router';

import srcRasLogo from '@/components/shared/ras-logo-512-emerald-600.png';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';

const openedNotifications: Notification[] = [];

const hasFocus = () => typeof document !== 'undefined' && document.hasFocus();

const useWindowFocus = () => {
  const [focused, setFocused] = useState(hasFocus); // Focus for first render

  useEffect(() => {
    setFocused(hasFocus()); // Focus for additional renders

    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return focused;
};

export function DesktopNotifications({ isMain }: { isMain?: boolean }) {
  const [show, setShow] = useState(window.Notification && Notification.permission !== 'granted');
  const focused = useWindowFocus();
  const idle = useIdleContext();

  const isPwa = useMediaQuery('(display-mode: standalone)');
  const [pollTime, setPollTime] = useState<number | null>(isPwa ? 5000 : null);
  const pollCount = useRef(isPwa ? 0 : 121);

  useInterval(() => {
    // On PWA, we may need to poll until they give the whole PWA notification permission on macos
    if (!isPwa || !show || pollCount.current > 120) {
      pollCount.current = 121;
      setPollTime(null);

      return;
    }

    if (Notification.permission === 'granted') {
      pollCount.current = 121;

      const notif = new Notification('RAS', {
        body: 'Thank you for enabling desktop notifications.',
        icon: srcRasLogo,
        tag: 'ras-enabled',
      });

      notif.onclick = function () {
        window.focus();
        this.close();
      };

      setPollTime(null);
      setShow(false);
    }
  }, pollTime);

  // biome-ignore lint/correctness/useExhaustiveDependencies(isPwa): doesn't change
  const enable = useCallback(() => {
    if (isPwa) {
      pollCount.current = 0;
      setPollTime(2000);
    }

    Notification.requestPermission((permission) => {
      if (permission === 'granted') {
        pollCount.current = 121;

        const notif = new Notification('RAS', {
          body: 'Thank you for enabling desktop notifications.',
          icon: srcRasLogo,
          tag: 'ras-enabled',
        });

        notif.onclick = function () {
          window.focus();
          this.close();
        };

        setPollTime(null);
        setShow(false);
      }
    });
  }, []);

  useEffect(() => {
    clearCurrentPageNotifications();
  }, []);

  useUpdateEffect(() => {
    if (!idle.inactive) {
      clearCurrentPageNotifications();
    }
  }, [idle.inactive]);

  useEffect(() => {
    const visible = !document.hidden && document.hasFocus();

    if (visible && focused) {
      clearCurrentPageNotifications();
    }
  }, [focused]);

  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isApp = !isDesktop || isPwa;

  // if (isMain && !isPwa) {
  if (isMain) {
    return;
  }

  return (
    <>
      {show && (
        <button
          onClick={enable}
          className={clsx(
            'group mx-auto flex h-10 shrink grow-0 flex-row items-center space-x-2 self-center rounded-sm p-2 text-left',
            'bg-zinc-800 text-white hover:bg-zinc-700 focus:bg-zinc-700',
            !isMain && 'relative',
            isMain && 'fixed top-3 z-50 opacity-50',
          )}
          title={`Enable ${isApp ? 'app' : 'desktop'} notifications`}
        >
          <BellIcon height={20} className="h-5 w-auto" aria-hidden="true" />
          <span
            className={clsx(
              'absolute top-0 left-5 z-1 flex h-full items-center overflow-hidden whitespace-nowrap rounded-sm px-2 text-xs leading-9 transition-[width,opacity] group-hover:opacity-100 group-focus:opacity-100',
              !isMain && 'w-0 max-w-fit opacity-0 group-hover:w-48 group-focus:w-48',

              'bg-zinc-800 text-zinc-200 group-hover:bg-zinc-700 group-focus:bg-zinc-700',
            )}
          >
            Enable {isApp ? 'app' : 'desktop'} notifications
          </span>
        </button>
      )}
    </>
  );
}

export function clearPageNotifications(url: string) {
  for (let i = openedNotifications.length; i--; ) {
    const notification = openedNotifications[i];

    if (notification.data?.url && url === notification.data.url) {
      try {
        openedNotifications.splice(i, 1);
        notification.close?.();
      } catch (error) {
        console.log(error);
      }
    }
  }
}

export function clearCurrentPageNotifications() {
  if (!IS_SERVER) {
    clearPageNotifications(window.location.pathname);
  }
}

export function openNotification(
  title: string,
  body?: string,
  url?: string,
  tag?: string,
  silent?: boolean,
) {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notif = new Notification(title, {
        body,
        data: {
          url,
        },
        icon: srcRasLogo,
        tag: tag || url,
        renotify: tag ? false : true,
        silent,
      });

      if (url) {
        openedNotifications.push(notif);

        notif.onclick = function (event) {
          window.focus();
          navigate(url);
          this.close?.();
          clearCurrentPageNotifications();
          event.preventDefault();
        };

        notif.onclose = function () {
          const index = openedNotifications.indexOf(this);

          if (index > -1) {
            openedNotifications.splice(index, 1);
          }
        };
      } else {
        notif.onclick = function (event) {
          window.focus();
          this?.close?.();
          event.preventDefault();
        };
      }

      return true;
    } catch (error) {
      console.error(error);
    }
  }

  return false;
}
